import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import classes from "./Dashboard.module.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import getLandedDashboardSummary from "../../API/landedDashboardSummaryApi";
import getLandedDashboardSkuCounts from "../../API/landedDashboardSkuCountsApi";

const DASHBOARD_MONTH_YEAR_STORAGE_KEY = "dashboardMonthYear";
const DASHBOARD_SUMMARY_STORAGE_PREFIX = "dashboardSummary:";
const DASHBOARD_SKU_COUNTS_STORAGE_KEY = "dashboardSkuCounts";

const SUMMARY_CARD_CONFIG = [
  { label: "No. of items repriced", key: "totalItemsRepriced" },
  { label: "No. of items recalculated", key: "totalItemsReCalculated" },
  { label: "Percentage change in landed cost", key: "totalChgPercentLandedCost" },
  { label: "Dollar change in landed cost", key: "totalChgAmtLandedCost" },
  { label: "Percentage change in DC cost", key: "totalChgPercentDCCost" },
  { label: "Dollar change in DC cost", key: "totalChgAmtDCCost" },
];

const METRIC_SECTIONS = [
  {
    title: "Pricing",
    description: "Pricing-related workflow activity.",
    metrics: [
      { id: "pricing-skus", label: "Pricing for new SKUs", key: "newRePricingSkusCount", itemType: "REPRICENEWSKU" },
      { id: "pricing-escalated", label: "Escalated SKUs", key: "escalatedCount", itemType: "ESCALATED" },
      { id: "pricing-flagged", label: "Flagged", key: "flaggedCount", route: "/flagged-items" },
      { id: "pricing-hold", label: "On hold SKUs", key: "onHoldCount", itemType: "ONHOLD" },
    ],
  },
  {
    title: "Sourcing",
    description: "Sourcing-related workflow activity.",
    metrics: [
      { id: "sourcing-priority", label: "Priority SKUs", key: "prioritySkusCount", itemType: "PRIORITY" },
      { id: "sourcing-other", label: "Other SKUs", key: "nonPrioritySkusCount", itemType: "OTHER" },
      { id: "sourcing-landing", label: "Landing for new SKUs", key: "newLandingSkusCount", itemType: "LANDNEWSKU" },
      { id: "sourcing-escalated", label: "Escalated SKUs" },
    ],
  },
];

const getCurrentMonthValue = () => new Date();

const parseStoredMonthYear = (storedValue) => {
  if (!storedValue) {
    return getCurrentMonthValue();
  }

  const parsedValue = new Date(storedValue);
  return Number.isNaN(parsedValue.getTime()) ? getCurrentMonthValue() : parsedValue;
};

const readStoredJson = (storageKey) => {
  try {
    const storedValue = sessionStorage.getItem(storageKey);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch {
    return null;
  }
};

const writeStoredJson = (storageKey, value) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage write failures and fall back to network fetches.
  }
};

const isPageReload = () => {
  const navigationEntry = typeof performance !== "undefined" ? performance.getEntriesByType?.("navigation")?.[0] : null;
  if (navigationEntry?.type) {
    return navigationEntry.type === "reload";
  }

  return typeof performance !== "undefined" && performance.navigation?.type === 1;
};

const formatMonthYear = (date) => (date ? format(date, "MM/yyyy") : "");

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoreDashboardData = !isPageReload() && location.state?.restoreDashboardData === true;
  const initialMonthYear = restoreDashboardData
    ? parseStoredMonthYear(sessionStorage.getItem(DASHBOARD_MONTH_YEAR_STORAGE_KEY))
    : getCurrentMonthValue();
  const initialSummaryStorageKey = `${DASHBOARD_SUMMARY_STORAGE_PREFIX}${formatMonthYear(initialMonthYear)}`;
  const initialSummaryData = restoreDashboardData ? readStoredJson(initialSummaryStorageKey) : null;
  const initialSkuCountsData = restoreDashboardData ? readStoredJson(DASHBOARD_SKU_COUNTS_STORAGE_KEY) : null;

  const [monthYear, setMonthYear] = useState(() =>
    initialMonthYear
  );
  const [summaryData, setSummaryData] = useState(() => initialSummaryData);
  const [isLoadingSummary, setIsLoadingSummary] = useState(() => !initialSummaryData);
  const [skuCountsData, setSkuCountsData] = useState(() => initialSkuCountsData);
  const [isLoadingSkuCounts, setIsLoadingSkuCounts] = useState(() => !initialSkuCountsData);

  useEffect(() => {
    sessionStorage.setItem(DASHBOARD_MONTH_YEAR_STORAGE_KEY, monthYear.toISOString());
  }, [monthYear]);

  useEffect(() => {
    const summaryStorageKey = `${DASHBOARD_SUMMARY_STORAGE_PREFIX}${formatMonthYear(monthYear)}`;
    const cachedSummary = restoreDashboardData ? readStoredJson(summaryStorageKey) : null;

    if (cachedSummary) {
      setSummaryData(cachedSummary);
      setIsLoadingSummary(false);
      return undefined;
    }

    let isActive = true;

    const loadSummary = async () => {
      setIsLoadingSummary(true);
      const response = await getLandedDashboardSummary(formatMonthYear(monthYear));

      if (!isActive) {
        return;
      }

      setSummaryData(response);
      writeStoredJson(summaryStorageKey, response);
      setIsLoadingSummary(false);
    };

    loadSummary();

    return () => {
      isActive = false;
    };
  }, [monthYear, restoreDashboardData]);

  useEffect(() => {
    const cachedSkuCounts = restoreDashboardData ? readStoredJson(DASHBOARD_SKU_COUNTS_STORAGE_KEY) : null;

    if (cachedSkuCounts) {
      setSkuCountsData(cachedSkuCounts);
      setIsLoadingSkuCounts(false);
      return undefined;
    }

    let isActive = true;

    const loadSkuCounts = async () => {
      setIsLoadingSkuCounts(true);
      const response = await getLandedDashboardSkuCounts();

      if (!isActive) {
        return;
      }

      setSkuCountsData(response);
      writeStoredJson(DASHBOARD_SKU_COUNTS_STORAGE_KEY, response);
      setIsLoadingSkuCounts(false);
    };

    loadSkuCounts();

    return () => {
      isActive = false;
    };
  }, [restoreDashboardData]);

  const summaryCards = useMemo(
    () =>
      SUMMARY_CARD_CONFIG.map((card) => ({
        ...card,
        value: isLoadingSummary ? "Loading..." : formatValue(summaryData?.[card.key]),
      })),
    [isLoadingSummary, summaryData]
  );

  const metricSections = useMemo(
    () =>
      METRIC_SECTIONS.map((section) => ({
        ...section,
        metrics: section.metrics.map((metric) => {
          const rawValue = skuCountsData?.[metric.key];
          return {
            ...metric,
            value: isLoadingSkuCounts ? "Loading..." : formatValue(rawValue),
            canOpenDetails: Boolean(metric.itemType || metric.route) && Number(rawValue) > 0,
          };
        }),
      })),
    [isLoadingSkuCounts, skuCountsData]
  );

  const handleMetricClick = (itemType, route, canOpenDetails) => {
    if (!canOpenDetails) {
      return;
    }

    navigate(route || `/dashboard/details/${itemType}`);
  };

  return (
    <section className={classes.page}>
      <header className={classes.header}>
        <div>
          <p className={classes.kicker}>Cost management overview</p>
          <h1>Dashboard</h1>
          <p className={classes.subtitle}>
            Quick snapshot of the latest landed cost activity and workflow status.
          </p>
        </div>
      </header>

      <section className={classes.cardSection}>
        <div className={classes.summaryHeader}>
          <h2 className={classes.sectionTitle}>Summary</h2>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className={classes.filterGroup}>
              <DatePicker
                label="Month / Year"
                views={["year", "month"]}
                openTo="month"
                value={monthYear}
                onChange={(value) => setMonthYear(value)}
                format="MMMM yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    className: classes.monthYearInput,
                    placeholder: "Select month and year",
                    inputProps: { "aria-label": "Month and year filter" },
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        </div>

        <div className={classes.summaryGrid}>
          {summaryCards.map((card) => (
            <article key={card.label} className={classes.summaryCard}>
              <p className={classes.cardLabel}>{card.label}</p>
              <p className={classes.cardValue}>{card.value}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={classes.circleSection}>
        <h2 className={classes.sectionTitle}>Workflow snapshot</h2>
        <div className={classes.metricSections}>
          {metricSections.map((section) => (
            <div key={section.title} className={classes.metricSection}>
              <div className={classes.tabPanelHeader}>
                <h3 className={classes.tabPanelTitle}>{section.title}</h3>
                <p className={classes.tabPanelDescription}>{section.description}</p>
              </div>

              <div className={classes.circleGrid}>
                {section.metrics.map((metric) => {
                  const circle = (
                    <div className={classes.circle}>
                      <span className={classes.circleValue}>{metric.value}</span>
                    </div>
                  );

                  return (
                    <div key={metric.id} className={classes.circleCard}>
                      {metric.canOpenDetails ? (
                        <button
                          type="button"
                          className={classes.circleButton}
                          aria-label={`${metric.label} details`}
                          onClick={() => handleMetricClick(metric.itemType, metric.route, metric.canOpenDetails)}
                        >
                          {circle}
                        </button>
                      ) : (
                        circle
                      )}
                      <p className={classes.circleLabel}>{metric.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
};

export default Dashboard;
